import React, { useEffect, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { cn } from './ui/utils';
import {
  MoreVertical,
  Calendar as CalendarIcon,
  Plus,
  Instagram as InstagramIcon,
  Globe,
  Clock,
  Sparkles,
  FileText,
  UserCheck,
  ArrowUpRight,
  Mail,
  Phone,
  MessageCircle,
  Tv,
  Monitor,
  Youtube,
  Music2,
  Loader2,
  LineChart,
  LayoutGrid,
} from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import type { AuthAccountManager } from '@/utils/auth';
import { useAuth } from '@/contexts/AuthContext';
import { useDashboardRecentCampaigns, type DashboardCampaignCardData } from '@/hooks/useDashboardRecentCampaigns';
import { useDashboardCalendarNextEvent } from '@/hooks/useDashboardCalendarNextEvent';
import { useDashboardRecentTeamMentions } from '@/hooks/useDashboardRecentTeamMentions';
import { useDashboardNotifications } from '@/hooks/useDashboardNotifications';
import { prefetchContentHub } from '@/utils/contentHubPrefetch';
import { prefetchCampaigns } from '@/utils/campaignsPrefetch';
import { prefetchCampaignDrafts } from '@/hooks/useDrafts';
import { TrendsForYouWidget } from './trends/TrendsForYouWidget';
import type { ClientNotificationRow } from '@/api/beatlyApi';
import profilePhoto from 'figma:asset/1c5e366bfc2174911ade1521e52834b19b7961f2.png';

interface DashboardPageProps {
  userName: string;
  accountManager?: AuthAccountManager | null;
  onNavigate: (page: any) => void;
}

function formatMentionTime(value: unknown, t: (key: string, opts?: Record<string, number | string>) => string): string {
  let s = '';
  if (typeof value === 'string') {
    s = value;
  } else if (value && typeof value === 'object' && 'value' in value) {
    s = String((value as { value: unknown }).value);
  }
  if (!s) return t('dashboard.recentMentions.notAvailable');
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return t('dashboard.recentMentions.notAvailable');
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return t('dashboard.recentMentions.justNow');
  if (mins < 60) return t('dashboard.recentMentions.minutesAgo', { count: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 48) return t('dashboard.recentMentions.hoursAgo', { count: hrs });
  const days = Math.floor(hrs / 24);
  if (days < 60) return t('dashboard.recentMentions.daysAgo', { count: days });
  return d.toLocaleDateString();
}

function formatCalendarEventWhen(iso: string, t: (key: string) => string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return t('dashboard.calendar.timeUnavailable');
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(d);
}

function formatScheduledCampaignDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(d);
}

function notificationNavigatePath(row: ClientNotificationRow): string {
  const id = encodeURIComponent(row.campaign_id);
  if (row.action_type === 'REVIEW_DRAFTS') return `/campaigns/${id}/content-approval`;
  if (row.action_type === 'REVIEW_CURATION') return `/campaigns/${id}/profiles`;
  if (row.action_type === 'SUMMARY_READY') {
    return `/campaigns/${id}/results`;
  }
  return `/campaigns/${id}/overview`;
}

function MsTeamsGlyph({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M19.5 6.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" fill="currentColor" />
      <path d="M22 9h-4.5a.5.5 0 0 0-.5.5v5a2.5 2.5 0 0 0 5 0v-5a.5.5 0 0 0-.5-.5h.5Z" fill="currentColor" />
      <path d="M14.5 5a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" fill="currentColor" opacity="0.8" />
      <path d="M18 8H9a1 1 0 0 0-1 1v7a4.5 4.5 0 0 0 9 0V9.5A1.5 1.5 0 0 0 18 8Z" fill="currentColor" opacity="0.9" />
      <rect x="1" y="8" width="10" height="10" rx="1" fill="currentColor" />
      <path d="M8 11H4v1.5h1.5V16h1V12.5H8V11Z" fill="white" />
    </svg>
  );
}

function SlackGlyph({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
    </svg>
  );
}

interface AccountManagerContactProps {
  href?: string;
  enabled: boolean;
  ariaLabel: string;
  unavailableLabel: string;
  children: React.ReactNode;
}

function AccountManagerContact({ href, enabled, ariaLabel, unavailableLabel, children }: AccountManagerContactProps) {
  const box =
    'h-8 w-8 rounded-lg border flex items-center justify-center transition-colors shrink-0 text-[#667085]';
  if (enabled && href) {
    const isExternal = href.startsWith('http');
    return (
      <a
        href={href}
        {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        className={`${box} border-gray-200 hover:bg-[#be43ff]/5 hover:border-[#be43ff]/30`}
        aria-label={ariaLabel}
      >
        {children}
      </a>
    );
  }
  return (
    <button
      type="button"
      disabled
      className={`${box} border-gray-100 opacity-50 cursor-not-allowed`}
      aria-label={unavailableLabel}
      title={unavailableLabel}
    >
      {children}
    </button>
  );
}

type ChannelType = 'instagram' | 'tiktok' | 'ctv' | 'dooh' | 'youtube' | 'web';

const channelConfig: Record<ChannelType, { icon: React.ElementType; label: string; color: string; bg: string; ring: string }> = {
  instagram: { icon: InstagramIcon, label: 'Instagram', color: 'text-pink-600', bg: 'bg-pink-50', ring: 'ring-pink-200' },
  tiktok: { icon: Music2, label: 'TikTok', color: 'text-gray-900', bg: 'bg-gray-100', ring: 'ring-gray-200' },
  ctv: { icon: Tv, label: 'CTV', color: 'text-blue-600', bg: 'bg-blue-50', ring: 'ring-blue-200' },
  dooh: { icon: Monitor, label: 'DOOH', color: 'text-purple-600', bg: 'bg-purple-50', ring: 'ring-purple-200' },
  youtube: { icon: Youtube, label: 'YouTube', color: 'text-red-600', bg: 'bg-red-50', ring: 'ring-red-200' },
  web: { icon: Globe, label: 'Web', color: 'text-teal-600', bg: 'bg-teal-50', ring: 'ring-teal-200' },
};

const MAX_VISIBLE = 3;

const onTrackBadgeClass = {
  color: 'bg-emerald-500',
  textColor: 'text-emerald-700',
  bgColor: 'bg-emerald-50',
} as const;

const KanbanCard = ({
  campaignId,
  title,
  budget,
  channels,
  creators,
  onTrackLabel,
  budgetLabel,
  creatorsLabel,
  channelsLabel,
}: DashboardCampaignCardData & {
  onTrackLabel: string;
  budgetLabel: string;
  creatorsLabel: string;
  channelsLabel: string;
}) => {
  const { t } = useTranslation();
  const stCfg = onTrackBadgeClass;

  const visibleChannels = channels.slice(0, MAX_VISIBLE);
  const extraChannels = channels.length - MAX_VISIBLE;

  const visibleCreators = creators.slice(0, MAX_VISIBLE);
  const extraCreators = creators.length - MAX_VISIBLE;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          to={`/campaigns/${campaignId}`}
          aria-label={t('dashboard.recentCampaigns.openCampaignAria', { name: title })}
          className="block rounded-2xl no-underline text-inherit outline-none focus-visible:ring-2 focus-visible:ring-[#be43ff]/40 focus-visible:ring-offset-2"
        >
          <div className="bg-white rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.04),0_4px_12px_rgba(0,0,0,0.03)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.06),0_8px_24px_rgba(0,0,0,0.05)] transition-all duration-200 cursor-pointer group">
      {/* ── Header zone ── */}
      <div className="px-4 pt-4 pb-3">
        {/* Title row */}
        <div className="flex items-center justify-between gap-2 mb-2.5">
          <h4 className="text-[13px] text-[#101828] truncate" style={{ fontWeight: 600, letterSpacing: '-0.01em' }}>{title}</h4>
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ${stCfg.bgColor} shrink-0`}>
            <span className={`w-1.5 h-1.5 rounded-full ${stCfg.color}`} />
            <span className={`text-[9px] ${stCfg.textColor}`} style={{ fontWeight: 600, letterSpacing: '0.01em' }}>{onTrackLabel}</span>
          </span>
        </div>

        {/* Metadata pills */}
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-[#f8f9fa] text-[10px] text-[#667085]">
            {budgetLabel}
            <span className="text-[#101828]" style={{ fontWeight: 600 }}>{budget}</span>
          </span>
        </div>
      </div>

      {/* ── Distribution zone ── subtle background shift instead of hard divider */}
      <div className="mx-2 mb-2 px-2.5 py-3 rounded-xl bg-white">
        <div className="flex items-start">
          {/* Creators */}
          <div className="flex-1 min-w-0">
            <span className="text-[9px] text-[#98a2b3] tracking-wide block mb-2" style={{ fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>
              {creatorsLabel}
            </span>
            <div className="flex items-center">
              <div className="flex -space-x-1.5">
                {visibleCreators.map((creator, i) => (
                  <div
                    key={i}
                    className="w-[26px] h-[26px] rounded-full ring-[1.5px] ring-[#fafbfc] overflow-hidden bg-gray-200 transition-transform group-hover:scale-105"
                    title={creator.name}
                  >
                    <ImageWithFallback src={creator.avatar} alt={creator.name} className="w-full h-full object-cover" />
                  </div>
                ))}
                {extraCreators > 0 && (
                  <div className="w-[26px] h-[26px] rounded-full bg-[#eef0f3] ring-[1.5px] ring-white flex items-center justify-center">
                    <span className="text-[9px] text-[#667085]" style={{ fontWeight: 600 }}>+{extraCreators}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Visual separator */}
          <div className="w-px h-10 bg-gray-200/60 mx-3 self-center" />

          {/* Channels */}
          <div className="flex-1 min-w-0">
            <span className="text-[9px] text-[#98a2b3] tracking-wide block mb-2" style={{ fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>
              {channelsLabel}
            </span>
            <div className="flex items-center">
              <div className="flex -space-x-1">
                {visibleChannels.map((channel) => {
                  const cfg = channelConfig[channel];
                  const Icon = cfg.icon;
                  return (
                    <div
                      key={channel}
                      className={`w-[26px] h-[26px] rounded-full ${cfg.bg} ring-[1.5px] ring-[#fafbfc] flex items-center justify-center transition-transform group-hover:scale-105`}
                      title={cfg.label}
                    >
                      <Icon className={`w-3 h-3 ${cfg.color}`} />
                    </div>
                  );
                })}
                {extraChannels > 0 && (
                  <div className="w-[26px] h-[26px] rounded-full bg-[#eef0f3] ring-[1.5px] ring-white flex items-center justify-center">
                    <span className="text-[9px] text-[#667085]" style={{ fontWeight: 600 }}>+{extraChannels}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
          </div>
        </Link>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        sideOffset={4}
        className={cn(
          'z-50 max-w-xs border border-gray-200 bg-white px-3 py-2 text-left text-xs font-medium text-[#101828] shadow-md break-words text-balance',
          '[&>svg]:hidden'
        )}
      >
        {title}
      </TooltipContent>
    </Tooltip>
  );
};

/** Placeholder card while campaign list is loading; mirrors KanbanCard layout. */
function KanbanCardSkeleton() {
  const bar = 'rounded-md skeleton-wave';
  const circle = 'rounded-full skeleton-wave ring-[1.5px] ring-[#fafbfc]';
  return (
    <div
      className="bg-white rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.04),0_4px_12px_rgba(0,0,0,0.03)]"
      aria-hidden
    >
      <div className="px-4 pt-4 pb-3">
        <div className="flex items-center justify-between gap-2 mb-2.5">
          <div className={`h-3.5 flex-1 max-w-[70%] ${bar}`} />
          <div className={`h-5 w-14 rounded-full shrink-0 ${bar}`} />
        </div>
        <div className="flex items-center gap-2">
          <div className={`h-7 w-28 ${bar}`} />
        </div>
      </div>
      <div className="mx-2 mb-2 px-2.5 py-3 rounded-xl bg-white">
        <div className="flex items-start">
          <div className="flex-1 min-w-0">
            <div className={`h-2 w-12 mb-2 ${bar}`} />
            <div className="flex -space-x-1.5">
              <div className={`w-[26px] h-[26px] ${circle}`} />
              <div className={`w-[26px] h-[26px] ${circle}`} />
              <div className={`w-[26px] h-[26px] ${circle}`} />
            </div>
          </div>
          <div className="w-px h-10 bg-gray-200/60 mx-3 self-center" />
          <div className="flex-1 min-w-0">
            <div className={`h-2 w-14 mb-2 ${bar}`} />
            <div className="flex -space-x-1">
              <div className={`w-[26px] h-[26px] ${circle}`} />
              <div className={`w-[26px] h-[26px] ${circle}`} />
              <div className={`w-[26px] h-[26px] ${circle}`} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

type RecentCampaignCardLabels = {
  onTrackLabel: string;
  budgetLabel: string;
  creatorsLabel: string;
  channelsLabel: string;
  countLoadingAria: string;
};

const KanbanColumn = ({
  title,
  color,
  cards,
  cardLabels,
  isLoading = false,
}: {
  title: string;
  color: string;
  cards: DashboardCampaignCardData[];
  cardLabels: RecentCampaignCardLabels;
  isLoading?: boolean;
}) => {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [canScroll, setCanScroll] = React.useState(false);
  const [isAtBottom, setIsAtBottom] = React.useState(false);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const check = () => {
      setCanScroll(el.scrollHeight > el.clientHeight);
      setIsAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 4);
    };
    check();
    el.addEventListener('scroll', check);
    return () => el.removeEventListener('scroll', check);
  }, [cards, isLoading]);

  const displayCards = isLoading ? [] : cards;
  const showFade = !isLoading && canScroll && !isAtBottom;

  return (
    <div
      className={`flex-1 min-w-[200px] p-4 rounded-2xl ${color} flex flex-col`}
      aria-busy={isLoading}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-sm text-[#101828]" style={{ fontWeight: 700 }}>{title}</h3>
          {isLoading ? (
            <span
              className="inline-flex items-center justify-center bg-white/70 px-1.5 py-0.5 rounded-md min-w-[1.75rem] h-[1.35rem]"
              aria-label={cardLabels.countLoadingAria}
            >
              <Loader2 className="w-3.5 h-3.5 text-[#98a2b3] animate-spin shrink-0" aria-hidden />
            </span>
          ) : (
            <span className="text-[10px] text-[#98a2b3] bg-white/70 px-1.5 py-0.5 rounded-md" style={{ fontWeight: 600 }}>
              {cards.length}
            </span>
          )}
        </div>
        <Button variant="ghost" size="icon" className="h-6 w-6 text-gray-400">
          <MoreVertical className="w-4 h-4" />
        </Button>
      </div>
      <div className="relative flex-1 min-h-0">
        <div
          ref={scrollRef}
          className="space-y-3 max-h-[420px] overflow-y-auto pr-1"
          style={{ scrollbarWidth: 'thin', scrollbarColor: '#e5e7eb transparent' }}
        >
          {isLoading ? (
            <KanbanCardSkeleton />
          ) : (
            displayCards.map((card) => {
              const { countLoadingAria: _omitCountAria, ...labelsForCard } = cardLabels;
              return <KanbanCard key={card.campaignId} {...card} {...labelsForCard} />;
            })
          )}
        </div>
        {showFade && (
          <div className="absolute bottom-0 left-0 right-1 h-10 bg-gradient-to-t from-current to-transparent pointer-events-none rounded-b-xl" style={{ color: displayCards.length ? 'rgb(249 250 251 / 0.9)' : 'transparent' }} />
        )}
      </div>
    </div>
  );
};

export const DashboardPage: React.FC<DashboardPageProps> = ({ userName, accountManager, onNavigate }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Trends widget matches sidebar/search gating — staff preview only.
  const showTrendsWidget = user?.show_evolve === true;
  const queryClient = useQueryClient();

  // Warm the React Query cache for /content-hub and /campaigns during browser
  // idle time so navigating there from the dashboard renders instantly. Each
  // prefetch is a no-op if the data is already cached. requestIdleCallback
  // yields to the dashboard's own initial paint and queries; setTimeout is the
  // Safari fallback.
  useEffect(() => {
    type IdleCallback = (cb: () => void, opts?: { timeout: number }) => number;
    type CancelIdleCallback = (handle: number) => void;
    const win = window as Window & {
      requestIdleCallback?: IdleCallback;
      cancelIdleCallback?: CancelIdleCallback;
    };
    const run = () => {
      prefetchContentHub(queryClient);
      prefetchCampaigns(queryClient, user);
    };
    let idleHandle: number | null = null;
    let timeoutHandle: number | null = null;
    if (typeof win.requestIdleCallback === 'function') {
      idleHandle = win.requestIdleCallback(run, { timeout: 2000 });
    } else {
      timeoutHandle = window.setTimeout(run, 500);
    }
    return () => {
      if (idleHandle != null && typeof win.cancelIdleCallback === 'function') {
        win.cancelIdleCallback(idleHandle);
      }
      if (timeoutHandle != null) {
        window.clearTimeout(timeoutHandle);
      }
    };
  }, [queryClient, user]);

  const isClient = user?.is_client === true;
  const hasMentionEnabled = user?.team?.has_mention_enabled === true;
  const isRecentMentionsEmptyCta = isClient && !hasMentionEnabled;
  const mentionsQueryEnabled = isClient && hasMentionEnabled;
  const {
    data: teamMentions,
    isLoading: teamMentionsLoading,
    isError: teamMentionsError,
  } = useDashboardRecentTeamMentions(mentionsQueryEnabled);

  const { data: recentCampaigns, isLoading: recentCampaignsLoading, isError: recentCampaignsError } =
    useDashboardRecentCampaigns();

  const {
    data: calendarPage,
    isLoading: calendarLoading,
    isError: calendarError,
  } = useDashboardCalendarNextEvent();
  const nextCalendarEvent = calendarPage?.events?.[0];

  const {
    data: notificationsData,
    isLoading: notificationsLoading,
    isError: notificationsError,
  } = useDashboardNotifications();

  const recentCardLabels = useMemo(
    () => ({
      onTrackLabel: t('dashboard.recentCampaigns.onTrack'),
      budgetLabel: t('dashboard.recentCampaigns.budgetLabel'),
      creatorsLabel: t('dashboard.recentCampaigns.creatorsLabel'),
      channelsLabel: t('dashboard.recentCampaigns.channelsLabel'),
      countLoadingAria: t('dashboard.recentCampaigns.countLoadingAria'),
    }),
    [t]
  );

  const currentHour = new Date().getHours();
  const greeting = currentHour < 12 ? 'Good morning' : currentHour < 18 ? 'Good afternoon' : 'Good evening';

  const notificationRows = notificationsData?.notifications ?? [];
  const notificationCount = notificationRows.length;

  const unavailable = t('dashboard.accountManager.contactUnavailable');
  const amName = accountManager?.name ?? t('dashboard.accountManager.fallbackName');
  const amAvatar = accountManager?.picture_url ?? profilePhoto;
  const mailHref = accountManager?.email ? `mailto:${accountManager.email}` : undefined;
  const telHref = accountManager?.phone ? `tel:${accountManager.phone.replace(/\s/g, '')}` : undefined;

  /* Contact routes to the human account manager, shown in their card. */
  const accountManagerContacts = (
    <>
      <AccountManagerContact
        href={mailHref}
        enabled={Boolean(mailHref)}
        ariaLabel={t('dashboard.accountManager.contactEmail')}
        unavailableLabel={unavailable}
      >
        <Mail className="w-3.5 h-3.5" />
      </AccountManagerContact>
      <AccountManagerContact
        href={telHref}
        enabled={Boolean(telHref)}
        ariaLabel={t('dashboard.accountManager.contactPhone')}
        unavailableLabel={unavailable}
      >
        <Phone className="w-3.5 h-3.5" />
      </AccountManagerContact>
      <AccountManagerContact
        href={accountManager?.msteams_url}
        enabled={Boolean(accountManager?.msteams_url)}
        ariaLabel={t('dashboard.accountManager.contactMsteams')}
        unavailableLabel={unavailable}
      >
        <MsTeamsGlyph className="w-3.5 h-3.5" />
      </AccountManagerContact>
      <AccountManagerContact
        href={accountManager?.slack_url}
        enabled={Boolean(accountManager?.slack_url)}
        ariaLabel={t('dashboard.accountManager.contactSlack')}
        unavailableLabel={unavailable}
      >
        <SlackGlyph className="w-3.5 h-3.5" />
      </AccountManagerContact>
      <AccountManagerContact
        href={accountManager?.whatsapp_url}
        enabled={Boolean(accountManager?.whatsapp_url)}
        ariaLabel={t('dashboard.accountManager.contactWhatsapp')}
        unavailableLabel={unavailable}
      >
        <MessageCircle className="w-3.5 h-3.5" />
      </AccountManagerContact>
    </>
  );

  return (
    <div className="max-w-[1600px] mx-auto p-4 md:p-6 lg:p-8 bg-[#f8f9fa] min-h-screen space-y-4 md:space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
        {/* Welcome Banner */}
        <Card className="lg:col-span-8 bg-gradient-to-r from-[#2e024c] to-[#5a1a8a] border-0 shadow-lg rounded-[24px] overflow-hidden relative">
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'radial-gradient(circle at 80% 20%, #be43ff 0%, transparent 50%), radial-gradient(circle at 20% 80%, #a9f600 0%, transparent 50%)'
          }} />
          <CardContent className="p-8 relative z-10">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-[#a9f600] text-sm">{greeting},</span>
            </div>
            <h1 className="text-white text-[28px] lg:text-[36px]" style={{ fontWeight: 700 }}>
              Welcome, <span className="text-[#be43ff]">{userName}</span>
            </h1>
            <p className="text-white/60 text-sm mt-2 max-w-lg">
              {!isClient || notificationsError || notificationsLoading || notificationCount === 0 ? (
                t('dashboard.needsAttention.welcomeNoItems')
              ) : (
                <Trans
                  i18nKey="dashboard.needsAttention.welcomeWithCountRich"
                  values={{ count: notificationCount }}
                  components={{ highlight: <span className="text-[#a9f600] font-medium" /> }}
                />
              )}
            </p>
          </CardContent>
        </Card>

        {/* User Profile Card */}
        <Card className="lg:col-span-4 bg-white border-0 shadow-sm rounded-[24px] overflow-hidden">
          <CardContent className="p-6 flex flex-col items-center justify-center h-full">
            <div className="w-16 h-16 rounded-full overflow-hidden border-3 border-[#be43ff]/20 shadow-md mb-3">
              <ImageWithFallback
                src={amAvatar}
                alt={t('dashboard.accountManager.avatarAlt')}
                className="w-full h-full object-cover"
              />
            </div>
            <h3 className="text-[#101828] text-sm text-center" style={{ fontWeight: 700 }}>
              {amName}
            </h3>
            <p className="text-[#667085] text-xs mt-0.5">{t('dashboard.accountManager.roleLabel')}</p>
            <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
              {accountManagerContacts}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 2: Recent Campaigns + Recent Mentions */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
        {/* Recent Campaigns */}
        <Card className="lg:col-span-8 bg-white border-0 shadow-sm rounded-[24px] overflow-hidden">
          <CardHeader className="px-8 pt-8 pb-4 flex flex-row items-center justify-between">
            <CardTitle className="text-[18px] font-bold text-[#101828]">
              {t('dashboard.recentCampaigns.title')}
            </CardTitle>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onNavigate('campaigns')}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#be43ff] text-white text-xs hover:bg-[#a830e0] active:scale-[0.97] transition-all shadow-sm"
                style={{ fontWeight: 600 }}
              >
                <Plus className="w-3.5 h-3.5" />
                {t('dashboard.recentCampaigns.newCampaign')}
              </button>
              <button
                onClick={() => onNavigate('see-campaigns')}
                className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl text-xs text-[#be43ff] hover:bg-[#be43ff]/5 transition-colors"
                style={{ fontWeight: 600 }}
              >
                {t('dashboard.recentCampaigns.seeAll')}
                <ArrowUpRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </CardHeader>
          <CardContent className="px-8 pb-8">
            {recentCampaignsError ? (
              <p className="text-sm text-[#667085]" role="alert">
                {t('dashboard.recentCampaigns.error')}
              </p>
            ) : recentCampaignsLoading ? (
              <div
                className="flex flex-col md:flex-row gap-4"
                role="status"
                aria-busy="true"
                aria-label={t('dashboard.recentCampaigns.loading')}
              >
                <KanbanColumn
                  isLoading
                  title={t('dashboard.recentCampaigns.columnPlanning')}
                  color="bg-gray-50"
                  cards={[]}
                  cardLabels={recentCardLabels}
                />
                <KanbanColumn
                  isLoading
                  title={t('dashboard.recentCampaigns.columnRunning')}
                  color="bg-green-50/50"
                  cards={[]}
                  cardLabels={recentCardLabels}
                />
                <KanbanColumn
                  isLoading
                  title={t('dashboard.recentCampaigns.columnCompleted')}
                  color="bg-gray-50"
                  cards={[]}
                  cardLabels={recentCardLabels}
                />
              </div>
            ) : (
              <div className="flex flex-col md:flex-row gap-4">
                <KanbanColumn
                  title={t('dashboard.recentCampaigns.columnPlanning')}
                  color="bg-gray-50"
                  cards={recentCampaigns?.planning ?? []}
                  cardLabels={recentCardLabels}
                />
                <KanbanColumn
                  title={t('dashboard.recentCampaigns.columnRunning')}
                  color="bg-green-50/50"
                  cards={recentCampaigns?.running ?? []}
                  cardLabels={recentCardLabels}
                />
                <KanbanColumn
                  title={t('dashboard.recentCampaigns.columnCompleted')}
                  color="bg-gray-50"
                  cards={recentCampaigns?.completed ?? []}
                  cardLabels={recentCardLabels}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Mentions */}
        <Card className="lg:col-span-4 bg-white border-0 shadow-sm rounded-[24px] p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-[18px] font-bold text-[#101828]">{t('dashboard.recentMentions.title')}</h3>
          </div>
          {!isClient ? (
            <p className="text-sm text-[#667085]">{t('dashboard.recentMentions.clientOnly')}</p>
          ) : !hasMentionEnabled ? (
            <div
              className="rounded-2xl border border-[#be43ff]/20 bg-gradient-to-br from-white to-[#faf5ff] p-5 shadow-sm"
              role="region"
              aria-label={t('dashboard.recentMentions.ctaRegionAria')}
            >
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#a9f600] shrink-0" aria-hidden />
                  <p className="text-sm font-bold leading-snug text-[#101828]">{t('dashboard.recentMentions.ctaHeadline')}</p>
                </div>
                <p className="text-sm text-[#667085] leading-relaxed">{t('dashboard.recentMentions.ctaIntro')}</p>
                <ol className="space-y-2 text-xs text-[#344054]">
                  <li className="flex gap-2">
                    <span
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-[#a9f600]/25 text-[#a9f600] font-bold"
                      aria-hidden
                    >
                      1
                    </span>
                    <span>{t('dashboard.recentMentions.ctaStep1')}</span>
                  </li>
                  <li className="flex gap-2">
                    <span
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-[#a9f600]/25 text-[#a9f600] font-bold"
                      aria-hidden
                    >
                      2
                    </span>
                    <span>{t('dashboard.recentMentions.ctaStep2')}</span>
                  </li>
                  <li className="flex gap-2">
                    <span
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-[#a9f600]/25 text-[#a9f600] font-bold"
                      aria-hidden
                    >
                      3
                    </span>
                    <span>{t('dashboard.recentMentions.ctaStep3')}</span>
                  </li>
                </ol>
              </div>
            </div>
          ) : teamMentionsError ? (
            <p className="text-sm text-[#667085]" role="alert">
              {t('dashboard.recentMentions.error')}
            </p>
          ) : teamMentionsLoading ? (
            <div className="space-y-3" role="status" aria-label={t('dashboard.recentMentions.loading')}>
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="h-24 rounded-2xl border border-gray-100 bg-gray-50/80 animate-pulse" />
              ))}
            </div>
          ) : !teamMentions?.length ? (
            <p className="text-sm text-[#667085]">{t('dashboard.recentMentions.empty')}</p>
          ) : (
            <div className="space-y-3">
              {teamMentions.map((row) => {
                const handle = row.handle?.trim() || row.creator_id || t('dashboard.recentMentions.notAvailable');
                const displayName = handle.startsWith('@') ? handle : `@${handle}`;
                const plat = (row.platform || '').toUpperCase();
                const PlatformIcon =
                  plat === 'INSTAGRAM' ? InstagramIcon : plat === 'TIKTOK' ? Music2 : plat === 'YOUTUBE' ? Youtube : Globe;
                const platformLabel =
                  plat === 'INSTAGRAM'
                    ? t('dashboard.recentMentions.platformInstagram')
                    : plat === 'TIKTOK'
                      ? t('dashboard.recentMentions.platformTiktok')
                      : plat === 'YOUTUBE'
                        ? t('dashboard.recentMentions.platformYoutube')
                        : t('dashboard.recentMentions.platformOther');
                const kws = row.keywords.length > 0 ? row.keywords : row.keyword ? [row.keyword] : [];
                const firstKeyword = kws[0] ?? '';
                const openList = () => {
                  navigate(`/creators/creatorlist/${encodeURIComponent(row.creatorlist_id)}`);
                };
                return (
                  <button
                    key={row.mention_id || `${row.creatorlist_id}-${row.content_id}`}
                    type="button"
                    onClick={openList}
                    className="group w-full text-left p-3 bg-white border border-gray-50 rounded-2xl hover:bg-gray-50/50 hover:border-gray-100 transition-all cursor-pointer"
                    aria-label={t('dashboard.recentMentions.openListAria', { keyword: firstKeyword, handle: displayName })}
                  >
                    <div className="flex gap-3">
                      <div className="relative shrink-0 w-14 h-14">
                        <div className="w-12 h-12 rounded-xl overflow-hidden shadow-sm border border-white bg-gradient-to-br from-[#be43ff]/15 to-[#a9f600]/20 relative z-10 flex items-center justify-center">
                          {row.preview_url ? (
                            <ImageWithFallback
                              src={row.preview_url}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-[10px] font-bold text-[#5a1a8a]" aria-hidden>
                              {(firstKeyword || t('dashboard.recentMentions.keywordPreviewFallback')).slice(0, 3)}
                            </span>
                          )}
                        </div>
                        <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full border-2 border-white bg-gray-100 shadow-md z-20 overflow-hidden flex items-center justify-center">
                          {row.mugshot_url ? (
                            <ImageWithFallback
                              src={row.mugshot_url}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <PlatformIcon className="w-3.5 h-3.5 text-[#667085]" aria-hidden />
                          )}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0 space-y-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <h4 className="text-xs font-bold text-[#101828] truncate">{displayName}</h4>
                          <div className="flex items-center gap-1 shrink-0 px-1.5 py-0.5 bg-gray-100 rounded-full">
                            <PlatformIcon
                              className={
                                plat === 'INSTAGRAM'
                                  ? 'w-2.5 h-2.5 text-pink-600'
                                  : plat === 'TIKTOK'
                                    ? 'w-2.5 h-2.5 text-gray-900'
                                    : plat === 'YOUTUBE'
                                      ? 'w-2.5 h-2.5 text-red-600'
                                      : 'w-2.5 h-2.5 text-blue-500'
                              }
                              aria-hidden
                            />
                            <span className="text-[9px] font-bold text-gray-500">{platformLabel}</span>
                          </div>
                        </div>
                        <p className="text-[10px] text-[#667085] leading-relaxed line-clamp-2">
                          {t('dashboard.recentMentions.keywordLine', { count: kws.length, keywords: kws.join(', ') })}
                        </p>
                        <div className="flex items-center gap-1 pt-0.5">
                          <Clock className="w-2.5 h-2.5 text-gray-400 shrink-0" aria-hidden />
                          <span className="text-[9px] font-medium text-gray-500">
                            {formatMentionTime(row.date_created, t)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          {isClient ? (
            <button
              type="button"
              className="w-full mt-4 inline-flex items-center justify-center gap-1.5 rounded-xl h-10 text-xs text-[#be43ff] hover:bg-[#be43ff]/5 transition-colors"
              style={{ fontWeight: 600 }}
              onClick={() =>
                isRecentMentionsEmptyCta
                  ? navigate('/creators/lists/ambassadors')
                  : onNavigate('creators')
              }
            >
              {isRecentMentionsEmptyCta
                ? t('dashboard.recentMentions.setupMentions')
                : t('dashboard.recentMentions.viewAll')}
              <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
          ) : null}
        </Card>
      </div>

      {/* Row 3: Needs Attention + Calendar */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
        {/* Needs Attention */}
        <Card className="lg:col-span-8 bg-white border-0 shadow-sm rounded-[24px] p-8">
          <h3 className="text-[18px] font-bold text-[#101828] mb-6">{t('dashboard.needsAttention.title')}</h3>
          {!isClient ? (
            <p className="text-sm text-[#667085]">{t('dashboard.needsAttention.clientOnly')}</p>
          ) : notificationsError ? (
            <p className="text-sm text-[#667085]" role="alert">
              {t('dashboard.needsAttention.error')}
            </p>
          ) : notificationsLoading ? (
            <div className="space-y-3" role="status" aria-label={t('dashboard.needsAttention.loading')}>
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-[72px] rounded-2xl border border-gray-100 bg-gray-50/80 animate-pulse" />
              ))}
            </div>
          ) : notificationRows.length === 0 ? (
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-5 bg-gray-50/50 rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center shrink-0">
                <Sparkles className="w-5 h-5 text-[#a9f600]" aria-hidden />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-bold text-[#101828]">
                  {t('dashboard.needsAttention.emptyTitle')}
                </h4>
                <p className="text-xs text-gray-500 mt-0.5">
                  {t('dashboard.needsAttention.emptyDescription')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => onNavigate('content-hub')}
                aria-label={t('dashboard.needsAttention.emptyCtaContentHubAria')}
                className="inline-flex items-center justify-center gap-1.5 rounded-xl text-xs h-8 px-4 shrink-0 border border-gray-200 text-[#be43ff] hover:bg-[#be43ff]/5 hover:border-[#be43ff]/30 transition-all active:scale-[0.97]"
                style={{ fontWeight: 600 }}
              >
                {t('dashboard.needsAttention.emptyCtaContentHub')}
                <ArrowUpRight className="w-3.5 h-3.5" aria-hidden />
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {notificationRows.map((row) => {
                const Icon =
                  row.action_type === 'REVIEW_DRAFTS'
                    ? FileText
                    : row.action_type === 'REVIEW_CURATION'
                      ? UserCheck
                      : row.action_type === 'SUMMARY_READY'
                        ? LineChart
                        : LayoutGrid;
                const dateStartFmt = formatScheduledCampaignDate(row.date_start);
                const title =
                  row.action_type === 'REVIEW_DRAFTS'
                    ? t('dashboard.needsAttention.reviewDraftsTitle')
                    : row.action_type === 'REVIEW_CURATION'
                      ? t('dashboard.needsAttention.reviewCurationTitle')
                      : row.action_type === 'SUMMARY_READY'
                        ? t('dashboard.needsAttention.summaryReadyTitle')
                        : t('dashboard.needsAttention.campaignLaunchedTitle');
                const description =
                  row.action_type === 'REVIEW_DRAFTS'
                    ? t('dashboard.needsAttention.reviewDraftsDescription', {
                        count: row.count_drafts,
                        campaignName: row.campaign_name,
                      })
                    : row.action_type === 'REVIEW_CURATION'
                      ? t('dashboard.needsAttention.reviewCurationDescription', {
                          count: row.count_creators,
                          campaignName: row.campaign_name,
                        })
                      : row.action_type === 'SUMMARY_READY'
                        ? t('dashboard.needsAttention.summaryReadyDescription', { campaignName: row.campaign_name })
                        : t('dashboard.needsAttention.campaignLaunchedDescription', {
                            campaignName: row.campaign_name,
                            dateStart: dateStartFmt,
                          });
                const actionLabel =
                  row.action_type === 'REVIEW_DRAFTS'
                    ? t('dashboard.needsAttention.actionReviewDrafts')
                    : row.action_type === 'REVIEW_CURATION'
                      ? t('dashboard.needsAttention.actionReviewCreators')
                      : row.action_type === 'SUMMARY_READY'
                        ? t('dashboard.needsAttention.actionViewSummary')
                        : t('dashboard.needsAttention.actionOpenCampaign');
                const variant: 'secondary' | 'tertiary' =
                  row.action_type === 'REVIEW_DRAFTS' || row.action_type === 'REVIEW_CURATION' ? 'secondary' : 'tertiary';
                const path = notificationNavigatePath(row);
                return (
                  <div
                    key={`${row.action_type}-${row.campaign_id}-${row.date_sort}`}
                    className="flex items-center justify-between p-4 bg-gray-50/50 rounded-2xl hover:bg-gray-50 transition-colors group"
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shrink-0 shadow-sm">
                        <Icon className="w-5 h-5 text-[#be43ff]" aria-hidden />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-bold text-[#101828]">{title}</h4>
                        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      className={`inline-flex items-center justify-center rounded-xl text-xs h-8 px-4 shrink-0 ml-4 transition-all active:scale-[0.97] ${
                        variant === 'secondary'
                          ? 'bg-[#be43ff] text-white hover:bg-[#a830e0] shadow-sm'
                          : 'border border-gray-200 text-[#be43ff] hover:bg-[#be43ff]/5 hover:border-[#be43ff]/30'
                      }`}
                      style={{ fontWeight: 600 }}
                      onClick={() => navigate(path)}
                      // "Review drafts" is a strong intent signal: start the (slow on big campaigns)
                      // drafts request on hover/focus so Content Review opens on warm data.
                      onMouseEnter={
                        row.action_type === 'REVIEW_DRAFTS'
                          ? () => prefetchCampaignDrafts(queryClient, row.campaign_id)
                          : undefined
                      }
                      onFocus={
                        row.action_type === 'REVIEW_DRAFTS'
                          ? () => prefetchCampaignDrafts(queryClient, row.campaign_id)
                          : undefined
                      }
                      aria-label={t('dashboard.needsAttention.openNotificationAria', {
                        title,
                        campaignName: row.campaign_name,
                      })}
                    >
                      {actionLabel}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Calendar */}
        <Card className="lg:col-span-4 bg-white border-0 shadow-sm rounded-[24px] overflow-hidden">
          <CardHeader className="p-6">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-[18px] font-bold text-[#101828]">{t('dashboard.calendar.title')}</CardTitle>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl text-xs text-[#be43ff] hover:bg-[#be43ff]/5 transition-colors shrink-0"
                style={{ fontWeight: 600 }}
                onClick={() => navigate('/campaigns/calendar')}
              >
                {t('dashboard.calendar.viewAll')}
                <ArrowUpRight className="w-3.5 h-3.5" aria-hidden />
              </button>
            </div>
          </CardHeader>
          <CardContent className="px-6 pb-6">
            {!isClient ? (
              <p className="text-sm text-[#667085]">{t('dashboard.calendar.clientOnly')}</p>
            ) : calendarError ? (
              <p className="text-sm text-[#667085]" role="alert">
                {t('dashboard.calendar.error')}
              </p>
            ) : calendarLoading ? (
              <div
                className="bg-[#f8f9fa] rounded-[20px] p-4 space-y-3"
                role="status"
                aria-busy="true"
                aria-label={t('dashboard.calendar.loading')}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gray-200/80 animate-pulse shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-32 bg-gray-200/80 rounded animate-pulse" />
                    <div className="h-3 w-24 bg-gray-200/60 rounded animate-pulse" />
                  </div>
                </div>
                <div className="h-16 rounded-xl bg-white border border-gray-100 animate-pulse" />
              </div>
            ) : !nextCalendarEvent ? (
              <div className="bg-[#f8f9fa] rounded-[20px] p-4 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center shrink-0">
                    <CalendarIcon className="w-6 h-6 text-[#be43ff]" aria-hidden />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-[#101828]">{t('dashboard.calendar.title')}</h4>
                    <p className="text-xs text-[#667085] mt-0.5">{t('dashboard.calendar.empty')}</p>
                  </div>
                </div>
                <Link
                  to="/campaigns"
                  className="inline-flex items-center justify-center gap-1.5 w-full rounded-xl h-10 text-xs text-[#be43ff] hover:bg-[#be43ff]/5 transition-colors"
                  style={{ fontWeight: 600 }}
                  aria-label={t('dashboard.calendar.browseCampaignsAria')}
                >
                  {t('dashboard.calendar.browseCampaigns')}
                  <ArrowUpRight className="w-3.5 h-3.5" aria-hidden />
                </Link>
              </div>
            ) : (
              <div className="bg-[#f8f9fa] rounded-[20px] p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center shrink-0">
                    <CalendarIcon className="w-6 h-6 text-[#be43ff]" aria-hidden />
                  </div>
                  <div className="flex-1 min-w-0 space-y-1">
                    <p className="text-[10px] font-semibold text-[#667085]">
                      {formatCalendarEventWhen(nextCalendarEvent.date_start || nextCalendarEvent.date, t)}
                    </p>
                    <h4 className="text-sm font-bold text-[#101828] leading-snug">{nextCalendarEvent.title}</h4>
                    {(nextCalendarEvent.campaign_name || nextCalendarEvent.brand_name) ? (
                      <p className="text-xs text-gray-500 truncate">
                        {nextCalendarEvent.campaign_name || nextCalendarEvent.brand_name}
                      </p>
                    ) : null}
                  </div>
                </div>
                {nextCalendarEvent.campaign_id ? (
                  <Link
                    to={`/campaigns/${encodeURIComponent(nextCalendarEvent.campaign_id)}`}
                    className="flex items-center justify-center gap-1.5 w-full rounded-xl h-10 text-xs text-[#be43ff] bg-white border border-[#be43ff]/20 hover:bg-[#be43ff]/5 transition-colors"
                    style={{ fontWeight: 600 }}
                    aria-label={t('dashboard.calendar.openCampaignAria', { title: nextCalendarEvent.title })}
                  >
                    {t('dashboard.calendar.openCampaignLink')}
                    <ArrowUpRight className="w-3.5 h-3.5" aria-hidden />
                  </Link>
                ) : (
                  <Link
                    to="/campaigns"
                    className="inline-flex items-center justify-center gap-1.5 w-full rounded-xl h-10 text-xs text-[#be43ff] hover:bg-[#be43ff]/5 transition-colors"
                    style={{ fontWeight: 600 }}
                    aria-label={t('dashboard.calendar.browseCampaignsAria')}
                  >
                    {t('dashboard.calendar.browseCampaigns')}
                    <ArrowUpRight className="w-3.5 h-3.5" aria-hidden />
                  </Link>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 4: Trend radar — staff preview only (@beatly.com) */}
      {showTrendsWidget ? <TrendsForYouWidget navigate={navigate} /> : null}
    </div>
  );
};
